import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShare } from '../context/ShareContext';
import { useCommunity } from '../context/CommunityContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const REASONS = [
  ['spam', 'Spam oder Werbung'],
  ['unsafe', 'Unsicherer Inhalt'],
  ['fraud', 'Betrug oder Täuschung'],
  ['hygiene', 'Hygienebedenken'],
  ['harassment', 'Belästigung'],
  ['privacy', 'Datenschutzproblem'],
  ['other', 'Anderer Grund'],
];

const ReportDialog = ({ targetType, targetId, targetUserId, compact = false }) => {
  const { user } = useShare();
  const { createReport, toggleBlock, blockedIds } = useCommunity();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('spam');
  const [details, setDetails] = useState('');
  const [saving, setSaving] = useState(false);
  const [errorText, setErrorText] = useState('');
  const isBlocked = targetUserId ? blockedIds.has(String(targetUserId)) : false;

  const ensureLogin = () => {
    if (user) return true;
    navigate('/auth');
    return false;
  };

  const openDialog = () => {
    if (!ensureLogin()) return;
    if (!targetType || !targetId) {
      window.alert('Dieser Inhalt kann derzeit nicht eindeutig zugeordnet werden.');
      return;
    }
    setErrorText('');
    setOpen(true);
  };

  const submit = async () => {
    if (!ensureLogin()) return;
    setSaving(true);
    setErrorText('');
    try {
      await createReport(targetType, targetId, reason, details);
      setOpen(false);
      setDetails('');
      setReason('spam');
      window.alert('Danke. Die Meldung wurde an die Moderation übermittelt.');
    } catch (error) {
      console.error('Meldung konnte nicht gespeichert werden:', error);
      setErrorText(error.message || 'Die Meldung konnte nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const blockUser = async () => {
    if (!ensureLogin() || !targetUserId) return;
    if (!window.confirm(isBlocked ? 'Blockierung aufheben?' : 'Diesen Nutzer blockieren? Seine Angebote und Nachrichten werden ausgeblendet.')) return;
    try {
      await toggleBlock(targetUserId);
    } catch (error) {
      window.alert(error.message || 'Die Blockierung konnte nicht geändert werden.');
    }
  };

  return (
    <>
      <div className={`flex items-center gap-2 ${compact ? '' : 'flex-wrap'}`}>
        <button
          type="button"
          onClick={(event) => { event.stopPropagation(); openDialog(); }}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-red-600"
        >
          <SafeIcon icon={FiIcons.FiFlag} /> Melden
        </button>
        {targetUserId && targetUserId !== user?.id && (
          <button
            type="button"
            onClick={(event) => { event.stopPropagation(); blockUser(); }}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-500 hover:text-red-600"
          >
            <SafeIcon icon={isBlocked ? FiIcons.FiUserCheck : FiIcons.FiUserX} />
            {isBlocked ? 'Entblocken' : 'Blockieren'}
          </button>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/50 p-4" onClick={(event) => { event.stopPropagation(); setOpen(false); }}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Inhalt melden</h2>
              <button type="button" onClick={() => setOpen(false)} className="p-2 text-gray-400 hover:text-gray-700">
                <SafeIcon icon={FiIcons.FiX} />
              </button>
            </div>

            <label className="mb-2 block text-sm font-bold text-gray-700">Grund</label>
            <select value={reason} onChange={(event) => setReason(event.target.value)} className="mb-4 w-full rounded-xl border border-gray-200 p-3">
              {REASONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>

            <label className="mb-2 block text-sm font-bold text-gray-700">Zusätzliche Hinweise</label>
            <textarea
              value={details}
              onChange={(event) => setDetails(event.target.value)}
              rows={4}
              maxLength={1000}
              className="mb-3 w-full rounded-xl border border-gray-200 p-3"
              placeholder="Was ist passiert? Bitte keine unnötigen personenbezogenen Daten eintragen."
            />
            <div className="mb-5 text-right text-xs text-gray-400">{details.length}/1000</div>

            {errorText && (
              <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {errorText}
              </div>
            )}

            <button
              type="button"
              onClick={submit}
              disabled={saving}
              className="w-full rounded-xl bg-red-600 px-5 py-3 font-bold text-white hover:bg-red-700 disabled:opacity-50"
            >
              {saving ? 'Wird gesendet…' : 'Meldung absenden'}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportDialog;
